import { replaceInvoiceTotalPriceColumns } from '@features/invoices/utils/replaceInvoiceTotalPriceColumns'

import type { InvoicingSettingsFieldPatch } from '../../../writer'
import type { DocumentTypeSettingsFormValues } from '../types/documentTypeSettingsFormValues'
import type { DocumentTypeSettingsDefaultTexts } from './getDocumentTypeSettingsFormValues'

interface GetDocumentTypeSettingsFieldPatchOptions {
  defaultTexts: DocumentTypeSettingsDefaultTexts
  savedColumnNames: string[]
  savedValues: DocumentTypeSettingsFormValues
  values: DocumentTypeSettingsFormValues
}

// An empty message or footer note shows the translated default. Storing the default sentence
// itself would keep it in one language after a language change.
const getStoredText = (text: string, defaultText: string): string => {
  const trimmedText = text.trim()

  return trimmedText === defaultText ? '' : trimmedText
}

const setColumnVisibility = (columnNames: string[], columnName: string, isVisible: boolean): string[] => {
  const otherColumnNames = columnNames.filter((name) => name !== columnName)

  return isVisible ? [...otherColumnNames, columnName] : otherColumnNames
}

// The saved list holds every column, including ones this form never shows. Only the toggled
// columns change; the rest keep their order. Like the editor, a column switched on goes last.
const getColumnNames = ({
  savedColumnNames,
  savedValues,
  values,
}: GetDocumentTypeSettingsFieldPatchOptions): string[] | undefined => {
  let columnNames = savedColumnNames

  if (values.isDateColumnVisible !== savedValues.isDateColumnVisible) {
    columnNames = setColumnVisibility(columnNames, 'date', values.isDateColumnVisible)
  }

  if (values.isUnitColumnVisible !== savedValues.isUnitColumnVisible) {
    columnNames = setColumnVisibility(columnNames, 'unit', values.isUnitColumnVisible)
  }

  if (values.totalPriceColumns !== savedValues.totalPriceColumns) {
    columnNames = replaceInvoiceTotalPriceColumns(columnNames, values.totalPriceColumns)
  }

  return columnNames === savedColumnNames ? undefined : columnNames
}

// Only changed values go into the patch.
export const getDocumentTypeSettingsFieldPatch = (
  options: GetDocumentTypeSettingsFieldPatchOptions,
): InvoicingSettingsFieldPatch => {
  const { defaultTexts, savedValues, values } = options
  const fieldPatch: InvoicingSettingsFieldPatch = {}
  const columnNames = getColumnNames(options)
  const message = getStoredText(values.message, defaultTexts.message)
  const footerNote = getStoredText(values.footerNote, defaultTexts.footerNote)

  if (columnNames) {
    fieldPatch.columns = { default: columnNames.join(',') }
  }

  if (message !== getStoredText(savedValues.message, defaultTexts.message)) {
    fieldPatch.description = { default: message }
  }

  if (footerNote !== getStoredText(savedValues.footerNote, defaultTexts.footerNote)) {
    fieldPatch.footerText = { default: footerNote }
  }

  if (values.termsAndConditions !== savedValues.termsAndConditions) {
    fieldPatch.legalterms = { default: values.termsAndConditions }
  }

  if (values.shouldShowTermsAndConditions !== savedValues.shouldShowTermsAndConditions) {
    fieldPatch.legalterms = { ...fieldPatch.legalterms, visible: values.shouldShowTermsAndConditions }
  }

  return fieldPatch
}
